export default function PrintNoteContent({ result, createdAt }) {
  if (!result) return null;

  const formatDate = (d) => {
    try {
      return new Date(d).toLocaleDateString();
    } catch {
      return d;
    }
  };

  const renderField = (label, value) => (
    <div style={{ marginBottom: '0.5rem' }}>
      <strong>{label}:</strong> {value || "N/A"}
    </div>
  );

  const renderDiagnosisList = (diagnoses = []) =>
    diagnoses.length ? (
      <>
        <h3 style={{ marginTop: '1rem' }}>Diagnoses (ICD-10)</h3>
        {diagnoses.map((dx, idx) => (
          <div key={idx} style={{ marginBottom: '0.75rem' }}>
            <strong>Diagnosis {idx + 1}:</strong><br />
            <div><strong>Code:</strong> {dx.code || "N/A"}</div>
            <div><strong>Description:</strong> {dx.description || "N/A"}</div>
          </div>
        ))}
      </>
    ) : null;

  const renderReviewOfSystems = (result) => {
    const SYSTEM_LABELS = {
      constitutional: "Constitutional",
      skin: "Skin",
      ent: "ENT",
      cardiovascular: "Cardiovascular",
      respiratory: "Respiratory",
      gastrointestinal: "GI",
      genitourinary: "GU",
      musculoskeletal: "Musculoskeletal",
      neurological: "Neuro",
      psychiatric: "Psych",
      endocrine: "Endocrine",
      lymphatic: "Lymphatic",
      allergicAndImmunologic: "Allergy/Immunology"
    };

    return (
      <>
        <hr style={{ margin: '2rem 0' }} />
        <h3 style={{ marginTop: '1rem' }}>Review of Systems</h3>
        {Object.entries(SYSTEM_LABELS).map(([key, label]) => (
          <div key={key} style={{ marginBottom: '0.5rem' }}>
            <strong>{label}:</strong> {result[key] || "N/A"}
          </div>
        ))}
      </>
    );
  };

  const renderAddendums = (addendums = []) =>
    addendums.length ? (
      <>
        <hr style={{ margin: '2rem 0' }} />
        <h3 style={{ marginTop: '1rem' }}>Addendums</h3>
        {addendums.map((a, idx) => (
          <div key={idx} style={{ marginBottom: '1rem' }}>
            <strong>{a.addedBy} — {new Date(a.addedAt).toLocaleString()}</strong>
            <div>{a.text}</div>
          </div>
        ))}
      </>
    ) : null;

  // PCC clinical data text is one item per line (see formatPccClinicalItems in the backend); render as a real list.
  const renderClinicalItemList = (text) => {
    const items = String(text || "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    if (!items.length) return <div>N/A</div>;

    return (
      <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
        {items.map((item, idx) => (
          <li key={idx} style={{ marginBottom: '0.25rem' }}>
            {item}
          </li>
        ))}
      </ul>
    );
  };

  const isBehaviorTherapy = result.noteTemplate === "BEHAVIOR_THERAPY";

  const hasPccClinicalData =
    result.pccMedications || result.pccConditionsProblems || result.pccDiagnosticReports;

  const renderPccClinicalData = () =>
    hasPccClinicalData ? (
      <>
        <hr style={{ margin: '2rem 0' }} />
        <h3>PointClickCare Clinical Data</h3>
        <div style={{ marginBottom: '0.5rem' }}>
          <strong>PCC Medications:</strong>
          {renderClinicalItemList(result.pccMedications)}
        </div>
        <div style={{ marginBottom: '0.5rem' }}>
          <strong>PCC Conditions / Problems:</strong>
          {renderClinicalItemList(result.pccConditionsProblems)}
        </div>
        <div style={{ marginBottom: '0.5rem' }}>
          <strong>PCC Diagnostic Reports:</strong>
          {renderClinicalItemList(result.pccDiagnosticReports)}
        </div>
      </>
    ) : null;

  return (
    <div style={{ fontFamily: 'sans-serif', margin: '2rem', pageBreakAfter: 'always' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <img src="https://via.placeholder.com/120x60.png?text=Your+Logo" alt="Logo" style={{ height: '60px' }} />
        <div style={{ textAlign: 'right' }}>
          <strong>Printed:</strong> {new Date().toLocaleString()}
        </div>
      </div>

      <h2 style={{ borderBottom: '1px solid #ccc', paddingBottom: '0.5rem' }}>Patient Information</h2>
      {renderField("Name", result.name)}
      {renderField("Patient ID", result.patientId)}
      {renderField("Date of Birth", result.dateOfBirth)}
      {renderField("Age", result.age)}
      {renderField("Date of Service", formatDate(createdAt))}
      {renderField("Time In", result.timeIn)}
      {renderField("Time Out", result.timeOut)}
      {renderField(
  "Duration",
  result.duration
    ? `${result.duration} minutes${result.isManualTimeEntry ? " (Manual)" : ""}`
    : "N/A"
)}
      {renderField("Manual Time Entry", result.isManualTimeEntry ? "Yes" : "No")}
      {renderField("Verbal Consent", result.verbalConsent)}

      <hr style={{ margin: '2rem 0' }} />
      <h3>Social History</h3>
      {renderField("Occupation", result.occupation)}
      {renderField("Alcohol", result.alcohol)}
      {renderField("Drugs", result.drugs)}
      {renderField("Smoking", result.smoking)}
      {renderField("SDOH", result.sdoh)}

      <hr style={{ margin: '2rem 0' }} />
      {isBehaviorTherapy ? (
        <>
          <h3>Behavior Therapy Note</h3>
          {renderField("Presenting Problem", result.presentingProblem)}
          {renderField("Past Medical History", result.pastMedicalHistory)}
          {renderField("Family History", result.familyHistory)}
          {renderField("Medications", result.medications)}
          {renderField("Allergies", result.allergies)}
          {renderField("Mental Status Exam", result.mentalStatusExam)}
          {renderField("Risk Assessment", result.riskAssessment)}
          {renderField("Therapy Modality / Interventions Used", result.therapyModality)}
          {renderField("Patient Response to Treatment", result.patientResponse)}
          {renderField("Progress Toward Goals", result.progressTowardGoals)}
          {renderField("Homework / Between-Session Plan", result.homeworkPlan)}
          {renderField("Treatment Plan", result.treatmentPlan)}
        </>
      ) : (
        <>
          <h3>Medical Details</h3>
          {renderField("History of Present Illness", result.historyPresentIllness)}
          {renderField("Past Medical History", result.pastMedicalHistory)}
          {renderField("Family History", result.familyHistory)}
          {renderField("Medications", result.medications)}
          {renderField("Allergies", result.allergies)}
          {renderField("Physical Exam", result.physicalExam)}
          {renderField("Procedures", result.procedures)}
          {renderField("Assessment & Plan", result.planAssessment)}
        </>
      )}

      {renderDiagnosisList(result.diagnoses)}
      {renderPccClinicalData()}
      {!isBehaviorTherapy && renderReviewOfSystems(result)}
      {renderAddendums(result.addendums)}

      <hr style={{ margin: '2rem 0' }} />
      <div style={{ marginTop: '1rem' }}>
        <p><strong>Provider:</strong> {result.signedBy || "N/A"}</p>
        <p><strong>Electronic Signature:</strong> Signed Electronically</p>
        <p><strong>Signed On:</strong> {result.signedAt ? new Date(result.signedAt).toLocaleString() : "N/A"}</p>
      </div>
    </div>
  );
}

  